/**
 * /dashboard/timesheet/weeks/$weekStart — Manager / HR-admin week
 * detail (G3 T.3b; SOP §1.2; Phase 3b — 2026-06-01).
 *
 * URL contract:
 *   /dashboard/timesheet/weeks/{weekStart}                 — caller's own week
 *   /dashboard/timesheet/weeks/{weekStart}?employee_id=X   — a report's week
 *
 * For the self case (no `employee_id` query) the page can in principle
 * forward to the My-week landing surface — but to keep the URL stable
 * for back-button reliability we render the same read-only view of the
 * caller's own week here as well. The cross-employee deep-link is the
 * primary entry from the inbox row.
 *
 * Authorisation: the API call (GET /api/v1/timesheet/weeks/{ws}?
 * employee_id=X) is the boundary. The backend re-checks self / manager-
 * of / hr_admin and 403s an unauthorised peek. The page wires the 403
 * onto a friendly "no access" panel. Status / action footer logic uses
 * useAuth() role for the unlock button visibility — UI only.
 *
 * Read-only: WeeklyGrid is rendered with `readOnly` so a manager never
 * sees Save / Submit / Copy / Add-project — even though the underlying
 * week may technically be in `draft`. Manager Approve / Reject / Unlock
 * actions live in the ManagerActionFooter sibling, gated on status +
 * role.
 *
 * Graceful degradation while T.2c is being deployed in parallel:
 *   - 404 on the week endpoint → "API not deployed yet" panel.
 *   - 403 → "no access" panel (this is the only path where 403 is
 *     surfaced — the inbox shouldn't 403 since GET is auth-only).
 *
 * Migration note: port of
 * frontend/src/app/(dashboard)/dashboard/timesheet/weeks/[weekStart]/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - use(params) (Next.js) → Route.useParams() (TanStack)
 *   - useSearchParams (Next.js) → Route.useSearch() (TanStack, Zod-validated)
 *   - next/link → @tanstack/react-router Link (typed)
 */

import { useMemo } from "react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { z } from "zod";
import axios from "axios";
import {
  AlertTriangle,
  ArrowLeft,
  Clock,
  RefreshCw,
} from "lucide-react";
import { toApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useDirectory } from "@/hooks/use-directory";
import { useWeek } from "@/hooks/use-timesheet";
import { addDays, isoWeekStart } from "@/lib/timesheet-format";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { TimesheetFeatureGate } from "@/components/timesheet/timesheet-feature-gate";
import { WeeklyGrid } from "@/components/timesheet/weekly-grid";
import { ManagerActionFooter } from "@/components/timesheet/manager-action-footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

// Search-param schema. TanStack Router validates every nav into this
// route against the schema — `?employee_id=42` becomes a typed number,
// anything malformed is stripped. Mirrors the per-route Zod pattern
// Phase 3a established (e.g. _dashboard/dashboard.approvals.tsx).
const weekDetailSearchSchema = z.object({
  employee_id: z.coerce.number().int().positive().optional(),
});

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/timesheet/weeks/$weekStart",
)({
  validateSearch: weekDetailSearchSchema,
  component: ManagerWeekDetailRoute,
});

function statusOf(err: unknown): number | undefined {
  return axios.isAxiosError(err) ? err.response?.status : undefined;
}

function isNotFound(err: unknown): boolean {
  return statusOf(err) === 404;
}

function isForbidden(err: unknown): boolean {
  return statusOf(err) === 403;
}

function ManagerWeekDetailRoute() {
  const { weekStart: rawWeekStart } = Route.useParams();
  const { employee_id } = Route.useSearch();

  // Snap to the Monday of whatever ISO date we received — the URL is a
  // user-typeable surface so we defend against `?employee_id=42` paired
  // with a non-Monday date. The week-detail GET is keyed on the Monday.
  const weekStart = useMemo(
    () => isoWeekStart(rawWeekStart),
    [rawWeekStart],
  );

  const employeeId = employee_id ?? null;

  return (
    <TimeAndLeaveShell
      title="Week detail"
      description="Read-only review of a submitted timesheet week."
    >
      <TimesheetFeatureGate>
        <ManagerWeekDetail
          weekStart={weekStart}
          employeeId={employeeId}
        />
      </TimesheetFeatureGate>
    </TimeAndLeaveShell>
  );
}

function ManagerWeekDetail({
  weekStart,
  employeeId,
}: {
  weekStart: string;
  employeeId: number | null;
}) {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { user } = useAuth();
  const directory = useDirectory({ includeInactive: true });

  // employee_id is omitted from the request when the URL has none —
  // backend then scopes to self. Cache key explicitly distinguishes
  // (own vs. someone-else) via the hook's normalisation.
  const q = useWeek(
    weekStart,
    employeeId != null ? { employee_id: employeeId } : undefined,
  );

  const previousWeekStart = useMemo(
    () => addDays(weekStart, -7),
    [weekStart],
  );

  const isSelf = employeeId == null || employeeId === user?.user_id;
  const isHrAdmin =
    user?.role === "company_admin" || user?.role === "hr_manager";
  const endpointMissing = isNotFound(q.error);
  const forbidden = isForbidden(q.error);
  const hardError = q.error && !endpointMissing && !forbidden;

  // When an HR admin reaches this page via the inbox Review link the
  // employee_id is always present (even for their own week). In that
  // case we want the ManagerActionFooter visible and the name to read
  // as the employee's display name — not "you" — so the approve/reject
  // dialog copy is correct.
  const reviewingViaInbox = employeeId != null;
  const showManagerFooter = !isSelf || (isHrAdmin && reviewingViaInbox);

  const employeeName = useMemo(() => {
    if (!q.data) return "this employee";
    if (isSelf && !reviewingViaInbox) return "you";
    return directory.label(q.data.employee_id);
  }, [directory, isSelf, reviewingViaInbox, q.data]);

  return (
    <>
      <div className="mb-4 flex justify-end">
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/timesheet/inbox" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" />
            Back to inbox
          </Link>
        </Button>
      </div>

      {q.isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-72 w-full" />
        </div>
      )}

      {endpointMissing && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
              <Clock className="h-6 w-6" />
            </span>
            <div className="space-y-1">
              <p className="text-base font-medium text-gray-900">
                Timesheet isn&apos;t available yet
              </p>
              <p className="max-w-md text-sm text-muted-foreground">
                The timesheet API is still being deployed. This page will
                populate automatically once it is live — no action needed
                on your side.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {forbidden && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
              <AlertTriangle className="h-6 w-6" />
            </span>
            <div className="space-y-1">
              <p className="text-base font-medium text-gray-900">
                You don&apos;t have access to this timesheet
              </p>
              <p className="max-w-md text-sm text-muted-foreground">
                Only the employee, their direct manager, or HR admins can
                review this week.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {hardError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this week</AlertTitle>
          <AlertDescription className="flex flex-col gap-2">
            <span>{toApiError(q.error).message}</span>
            <span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => q.refetch()}
                className="gap-1.5"
              >
                <RefreshCw className="h-4 w-4" />
                Retry
              </Button>
            </span>
          </AlertDescription>
        </Alert>
      )}

      {q.data && !endpointMissing && !forbidden && !hardError && (
        <>
          <WeeklyGrid
            // Remount on (week_start, status) for the same reason the
            // employee surface does (drop stale local edit cache when a
            // meaningfully different snapshot lands — though in read-only
            // mode no cells are typable, the overlay / status banner
            // changes still warrant the remount).
            key={`mgr-${q.data.week_start_date}-${q.data.status}`}
            week={q.data}
            previousWeekStart={previousWeekStart}
            readOnly
          />
          {showManagerFooter && (
            <ManagerActionFooter
              week={q.data}
              employeeName={employeeName}
            />
          )}
        </>
      )}
    </>
  );
}
